// QuotationDeclarations.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500'
  }[type];

  const icon = {
    success: '✓',
    error: '✗',
    info: 'ℹ'
  }[type];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-80`}>
        <span className="text-xl">{icon}</span>
        <span className="flex-1">{message}</span>
        <button onClick={onClose} className="text-white hover:text-gray-200 text-lg">
          &times;
        </button>
      </div>
    </div>
  );
};

// Declaration Question Interface
interface DeclarationQuestion {
  id?: number;
  question: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

const QuotationDeclarations: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [questions, setQuestions] = useState<DeclarationQuestion[]>([
    { id: 1, question: 'Do you confirm that all information provided is accurate and complete?', is_default: true },
    { id: 2, question: 'Have you disclosed all material facts relevant to this quotation?', is_default: false },
    { id: 3, question: 'Do you authorize us to verify the information provided?', is_default: false },
    { id: 4, question: 'Do you understand that providing false information may invalidate your policy?', is_default: false },
    { id: 5, question: 'Are you aware that this quotation is subject to underwriting approval?', is_default: false },
  ]);
  
  const [newQuestion, setNewQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{ id: number; question: string } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Fetch declaration questions from API
  useEffect(() => {
    fetchDeclarationQuestions();
  }, []);

  const fetchDeclarationQuestions = async () => {
  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    console.log('🔍 Fetching declaration questions from:', `${API_URL}/api/quotation/declaration-questions`);
    
    const response = await fetch(`${API_URL}/api/quotation/declaration-questions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('📥 Fetch response status:', response.status);

    if (!response.ok) {
      console.log('⚠️ Using default declaration questions');
      return; // Keep using default questions
    }

    const data = await response.json();
    console.log('✅ Fetched declaration questions:', data);
    
    // 🚨 FIX: Handle different response formats
    let questionsData: DeclarationQuestion[] = [];
    
    if (data && data.success && Array.isArray(data.data)) {
      // Format 1: { success: true, data: [...] }
      questionsData = data.data;
    } else if (Array.isArray(data)) {
      // Format 2: Direct array [...]
      questionsData = data;
    } else if (data && data.success && data.data && typeof data.data === 'object') {
      // Format 3: { success: true, data: {...} } - single object
      questionsData = [data.data];
    } else {
      console.error('Unexpected response format:', data);
      return; // Keep using default questions
    }
    
    console.log(`📋 Setting ${questionsData.length} questions`);
    setQuestions(questionsData);
    
  } catch (err: any) {
    console.error('❌ Error fetching declaration questions:', err);
    console.log('⚠️ Using default declaration questions');
  } finally {
    setIsLoading(false);
  }
};

  // Add new declaration question
  const handleAddQuestion = async () => {
  if (!newQuestion.trim()) {
    showToast('Please enter a question', 'error');
    return;
  }

  // Check for duplicates
  if (questions.some(q => q.question.toLowerCase() === newQuestion.toLowerCase().trim())) {
    showToast('This question already exists', 'error');
    return;
  }

  setIsLoading(true);

  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const newDeclQuestion = {
      question: newQuestion.trim(),
      is_default: false
    };

    console.log('📤 Sending new question to API:', newDeclQuestion);

    // Try to save to API
    const response = await fetch(`${API_URL}/api/quotation/declaration-questions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newDeclQuestion),
    });

    console.log('📥 API Response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('✅ API Response data:', data);
      
      // 🚨 CRITICAL FIX: Handle different response formats properly
      let savedQuestion;
      
      if (data && data.success && data.data) {
        // Format 1: { success: true, data: { id, question, is_default } }
        savedQuestion = data.data;
      } else if (data && data.id) {
        // Format 2: Direct question object { id, question, is_default }
        savedQuestion = data;
      } else if (data && typeof data === 'object') {
        // Format 3: Some other object format
        savedQuestion = { ...newDeclQuestion, ...data };
      } else {
        // Fallback: Generate a temporary ID
        const newId = Math.max(...questions.map(q => q.id || 0), 0) + 1;
        savedQuestion = { ...newDeclQuestion, id: newId };
      }
      
      // 🚨 IMPORTANT: Make sure the savedQuestion has all required fields
      const finalQuestion: DeclarationQuestion = {
        id: savedQuestion.id || Math.max(...questions.map(q => q.id || 0), 0) + 1,
        question: savedQuestion.question || newQuestion.trim(),
        is_default: savedQuestion.is_default || false,
        created_at: savedQuestion.created_at,
        updated_at: savedQuestion.updated_at
      };
      
      console.log('💾 Final question to add:', finalQuestion);
      
      // Add to local state
      setQuestions(prev => [...prev, finalQuestion]);
      showToast('Declaration question added successfully!');
    } else {
      // Handle non-OK response
      const errorText = await response.text();
      console.error('❌ API Error response:', errorText);
      
      // Try to parse error JSON
      try {
        const errorData = JSON.parse(errorText);
        showToast(errorData.message || 'Failed to add question', 'error');
      } catch {
        showToast(`Failed to add question (Status: ${response.status})`, 'error');
      }
      
      // Still add to local state for immediate feedback
      const newId = Math.max(...questions.map(q => q.id || 0), 0) + 1;
      setQuestions(prev => [...prev, { 
        id: newId,
        question: newQuestion.trim(),
        is_default: false
      }]);
      showToast('Question added (offline mode)', 'info');
    }

    setNewQuestion('');
  } catch (err: any) {
    console.error('❌ Error adding declaration question:', err);
    // Add to local state anyway for offline mode
    const newId = Math.max(...questions.map(q => q.id || 0), 0) + 1;
    setQuestions(prev => [...prev, { 
      id: newId,
      question: newQuestion.trim(),
      is_default: false
    }]);
    setNewQuestion('');
    showToast('Declaration question added (offline mode)', 'info');
  } finally {
    setIsLoading(false);
  }
};

  // Start editing a question
  const handleStartEdit = (question: DeclarationQuestion) => {
    setIsEditing(question.id || null);
    setEditQuestion(question.question);
  };

  // Save edited question
  const handleSaveEdit = async (id: number) => {
    if (!editQuestion.trim()) {
      showToast('Question cannot be empty', 'error');
      return;
    }

    // Check for duplicates (excluding current item)
    if (questions.some(q => q.id !== id && q.question.toLowerCase() === editQuestion.toLowerCase().trim())) {
      showToast('Another question with this text already exists', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const updatedQuestion = {
        question: editQuestion.trim()
      };

      // Try to update via API
      await fetch(`${API_URL}/api/quotation/declaration-questions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedQuestion),
      });

      // Update local state
      setQuestions(prev => prev.map(q => 
        q.id === id ? { ...q, question: editQuestion.trim() } : q
      ));

      setIsEditing(null);
      setEditQuestion('');
      showToast('Question updated successfully!');
    } catch (err: any) {
      console.error('Error updating question:', err);
      // Update local state anyway
      setQuestions(prev => prev.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim()
        } : q
      ));
      setIsEditing(null);
      setEditQuestion('');
      showToast('Question updated successfully!');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(null);
    setEditQuestion('');
  };

  // Set default question
  const handleSetDefault = async (id: number) => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // Try to update via API
      await fetch(`${API_URL}/api/quotation/declaration-questions/${id}/set-default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Update local state
      setQuestions(prev => prev.map(q => ({
        ...q,
        is_default: q.id === id
      })));

      showToast('Default question updated successfully!');
    } catch (err: any) {
      console.error('Error setting default question:', err);
      // Update local state anyway
      setQuestions(prev => prev.map(q => ({
        ...q,
        is_default: q.id === id
      })));
      showToast('Default question updated successfully!');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove button click
  const handleRemoveClick = (id: number) => {
    console.log('🗑️ Attempting to remove question with ID:', id);
    
    const questionToRemove = questions.find(q => q.id === id);
    console.log('🔍 Found question:', questionToRemove);
    
    if (questionToRemove) {
      setQuestionToDelete({ id, question: questionToRemove.question });
      setShowConfirmModal(true);
    } else {
      console.error('❌ Question not found for ID:', id);
      showToast('Question not found', 'error');
    }
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
  if (!questionToDelete) return;
  
  const { id } = questionToDelete;
  setShowConfirmModal(false);
  setDeletingId(id); // Track which item is being deleted
  
  setIsLoading(true);

  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    console.log(`🗑️ Deleting question ID: ${id}`);
    
    const response = await fetch(`${API_URL}/api/quotation/declaration-questions/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log(`📥 Delete response status: ${response.status}`);
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Delete response data:', data);
      
      // ✅ FIX: Handle API response without "success" property
      // Your API returns: { message: "Declaration question deleted successfully" }
      if (data.message && data.message.includes('successfully')) {
        // Remove from state
        setQuestions(prev => {
          const newQuestions = prev.filter(q => q.id !== id);
          console.log(`✅ Removed question ${id}. New count: ${newQuestions.length}`);
          return newQuestions;
        });
        showToast('Question removed successfully!');
      } else if (data.success === false) {
        console.error('❌ API returned success:false:', data);
        showToast(data.message || 'Failed to remove question', 'error');
      } else {
        // If we get here but response was OK, assume success
        setQuestions(prev => prev.filter(q => q.id !== id));
        showToast('Question removed successfully!');
      }
    } else {
      const errorText = await response.text();
      console.error('❌ Delete failed with status:', response.status, errorText);
      // Still remove from state for UI consistency
      setQuestions(prev => prev.filter(q => q.id !== id));
      showToast('Question removed from local state', 'info');
    }
  } catch (err: any) {
    console.error('❌ Error removing question:', err);
    // Remove from state even on error
    setQuestions(prev => prev.filter(q => q.id !== id));
    showToast('Question removed (offline mode)', 'info');
  } finally {
    setIsLoading(false);
    setDeletingId(null);
    setQuestionToDelete(null);
  }
};

  // Cancel delete
  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setQuestionToDelete(null);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {/* Confirmation Modal for Delete */}
      {showConfirmModal && questionToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirm Delete
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove this declaration question?
              </p>
              <div className="p-3 bg-gray-100 rounded mb-4">
                <p className="font-medium text-gray-900">"{questionToDelete.question}"</p>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <Sidebar 
        isVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
      />

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header 
          title="Quotation Declarations"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        {/* Page Content */}
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => navigate('/process-definitions')}
              className="mb-6 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Process Definitions
            </button>

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Quotation Declaration Questions
              </h1>
              <p className="text-gray-600">
                Manage declaration questions that will appear on the Create Quote page. 
                These questions help ensure proper disclosure and compliance during the quotation process.
              </p>
            </div>

            {/* Add New Question Section */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Add New Declaration Question
              </h2>
              <div className="space-y-4">
                <div>
                  <textarea
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter a new declaration question (e.g., 'Do you confirm all information is accurate?')"
                    rows={3}
                    disabled={isLoading}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Tip: Keep questions clear and concise for better user understanding.
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleAddQuestion}
                    disabled={isLoading || !newQuestion.trim()}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Question
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Declaration Questions
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {questions.length} question{questions.length !== 1 ? 's' : ''} configured
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Default questions will be pre-selected on quote forms
                  </div>
                </div>
              </div>

              {isLoading && questions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-700">Loading declaration questions...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-4 text-gray-300">📝</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">No declaration questions yet</p>
                  <p className="text-gray-600">Add your first declaration question above to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {questions.map((question) => (
                    <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                      {isEditing === question.id ? (
                        // Edit Mode
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Edit Question
                            </label>
                            <textarea
                              value={editQuestion}
                              onChange={(e) => setEditQuestion(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              rows={3}
                              disabled={isLoading}
                            />
                          </div>
                          <div className="flex justify-between items-center">
                            <div>
                              {question.is_default && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  Default Question
                                </span>
                              )}
                            </div>
                            <div className="space-x-3">
                              <button
                                onClick={() => handleSaveEdit(question.id!)}
                                disabled={isLoading || !editQuestion.trim()}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save Changes
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={isLoading}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-4">
                              <div className="flex items-center mb-2">
                                <span className="text-gray-900">
                                  {question.question}
                                </span>
                                {question.is_default && (
                                  <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Default
                                  </span>
                                )}
                              </div>
                              {question.created_at && (
                                <p className="text-sm text-gray-500">
                                  Added: {new Date(question.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              {!question.is_default && (
                                <>
                                  <button
                                    onClick={() => handleSetDefault(question.id!)}
                                    disabled={isLoading}
                                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors flex items-center"
                                    title="Set as default question"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Set Default
                                  </button>
                                  <button
                                    onClick={() => handleStartEdit(question)}
                                    disabled={isLoading}
                                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex items-center"
                                    title="Edit question"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit
                                  </button>
                                  
                                  <button
                                    onClick={() => handleRemoveClick(question.id!)}
                                    disabled={isLoading || deletingId === question.id}
                                    className="px-3 py-1.5 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center"
                                    title="Remove question"
                                  >
                                    {deletingId === question.id ? (
                                      <>
                                        <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-red-700" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Removing...
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Remove
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                              {question.is_default && (
                                <button
                                  onClick={() => handleStartEdit(question)}
                                  disabled={isLoading}
                                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex items-center"
                                  title="Edit default question"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Information Card */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">About Quotation Declarations</h3>
                  <div className="text-blue-700 space-y-2">
                    <p className="text-sm">
                      Declaration questions are an important part of the quotation process. They help:
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Ensure customers provide accurate information</li>
                      <li>Meet compliance and regulatory requirements</li>
                      <li>Establish clear terms and conditions</li>
                      <li>Reduce disputes and misunderstandings</li>
                    </ul>
                    <div className="mt-4 p-3 bg-white bg-opacity-50 rounded border border-blue-200">
                      <p className="text-xs text-blue-800 font-medium mb-1">Best Practices:</p>
                      <ul className="text-xs text-blue-700 list-disc pl-4">
                        <li>Keep questions clear and unambiguous</li>
                        <li>Use simple language that customers understand</li>
                        <li>Focus on material facts and disclosures</li>
                        <li>Review and update questions regularly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuotationDeclarations;